import KeyboardTabIcon from "@mui/icons-material/KeyboardTab";
import Box from "@mui/material/Box";
import GroupAddOutlinedIcon from "@mui/icons-material/GroupAddOutlined";
import GroupsIcon from "@mui/icons-material/Groups";
import Button from "@mui/material/Button";
import MuiDrawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { alpha, styled, type Theme } from "@mui/material/styles";
import { skipToken } from "@reduxjs/toolkit/query";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import Loader from "_shared/_views/loading/loader";
import EntityMember from "_shared/entity-members/entity-member";
import TeamRosterTable, {
  TeamRosterSection,
  TEAM_LEADER_ROLE,
  TEAM_MEMBER_ROLE,
  TEAM_ROSTER_PERSON_COLUMNS,
  teamRosterCell,
  type TeamRosterAddAction,
  type TeamRosterRow,
} from "_shared/entity-members/_views/team-roster-table";
import RowLink from "_shared/_views/tables/row-link";
import Guid from "_shared/guids/guid";
import IndividualAvatar from "marketplace/_views/avatars/individual-avatar";
import { useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import Individual from "marketplace/individuals/individual";
import ViewIndividualProfile from "marketplace/individuals/profiles/_views/individual-profile-container";
import MarketplaceTeam from "marketplace/teams/team";
import { enqueueSnackbar } from "notistack";
import { getSessionPermissions } from "permissions/_stores/permissions-redux-slice";
import React, { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { NavigateFunction } from "react-router";
import { useSession } from "users/__session/session-context";
import { AccountType } from "users/account-types/account-type";
import ProposalFieldHistoryButton from "work/proposals/negotiation/proposal-field-history-button";
import {
  proposalTeamMemberItemId,
  useRemovedProposalTeamMembers,
  type ProposalTeamRemovedMember,
} from "work/proposals/negotiation/proposal-team-removed-members";
import ProposalFieldTimelineDropdown, {
  type ProposalFieldTimelineRevertEntry,
} from "work/proposals/negotiation/proposal-field-timeline-dropdown";
import {
  proposalCollectionItemRevertValue,
  proposalScalarFieldRevertValue,
} from "work/proposals/negotiation/proposal-collection-item-revert";
import {
  fieldHasPendingChange,
  pendingChangeListItemStyles,
  pendingChangeOutlinedFieldStyles,
  pendingChangeTableRowStyles,
} from "work/proposals/comments/_view/pending-change-field-highlight";
import ProposalField, { ProposalFieldName } from "work/proposals/proposal-fields/proposal-field";
import ProposalDeleteActionButton from "work/proposals/_views/_shared/proposal-delete-action-button";
import useAddAllColleagues, {
  ADD_ALL_COLLEAGUES_LABEL,
} from "work/work-agreements/teams/vendor-team/_views/members/add-all-colleagues-action";
import {
  mayAddAllColleagues,
  rosterLegalEntityId,
} from "work/work-agreements/teams/vendor-team/_views/members/add-all-colleagues";
import { ReviewerEditLockIcon } from "work/proposals/reviewers/_views/reviewer-edit-lock-feedback";
import TeamLeaderSelector from "work/work-agreements/teams/vendor-team/_views/leader/team-leader-selector";
import VendorTeamSelection from "work/work-agreements/teams/vendor-team/_views/marketplace/marketplace-vendor-team-selector";
import VendorTeamMemberSelection from "work/work-agreements/teams/vendor-team/_views/members/vendor-team-member-selection";

const MainContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  marginTop: theme.spacing(1),
}));
const TeamControls = styled("div")(({ theme }) => ({
  alignItems: "stretch",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
  marginBottom: theme.spacing(1),
  width: "100%",
}));
const TeamControlsGroup = styled("div")(({ theme }) => ({
  alignItems: "flex-start",
  display: "flex",
  flexWrap: "wrap",
  gap: theme.spacing(1),
}));
const TeamContainer = styled(Box)(({ theme }) => ({
  columnGap: theme.spacing(10),
  display: "flex",
  flexDirection: "row",
  justifyContent: "flex-start",
  paddingBottom: theme.spacing(3),
  rowGap: theme.spacing(3),
  width: "100%",
  [theme.breakpoints.down("lg")]: {
    columnGap: theme.spacing(3),
  },
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    paddingBottom: 0,
  },
}));
/**
 * Says why the leader controls are absent for an actor who may still edit the roster. Without it the
 * two halves of the team look arbitrarily inconsistent — members editable, leader not — and a
 * reviewer is left to conclude the page is broken (#4854).
 */
const TEAM_IDENTITY_LOCK_MESSAGE =
  "Only the proposal creator can change the team leader or choose a different team, and only " +
  "before the proposal is first submitted. You can still change who is on the team.";
const TeamIdentityLockNotice = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontStyle: "italic",
  paddingTop: theme.spacing(1),
}));
/**
 * What a member's row says when the roster holds them but the directory could not name them. Dropping
 * the row instead — which this view did until #4955 — told the actor the team was one person smaller
 * than it is, and a person nobody can see is a person nobody can remove. Naming them by their raw
 * userId is not the alternative (#4830): a guid identifies nobody to a reader.
 */
const UNNAMED_MEMBER = "Unnamed member";
/** Room for the two controls a draft roster row carries: remove, and the field's own history. */
const ROSTER_ACTIONS_COLUMN_WIDTH = 120;
/**
 * What the roster's add control calls "take this team's people from a marketplace team".
 *
 * Exported so the browser harness names it exactly once: it used to be a button above the roster and
 * is now an entry behind the add control's dropdown (#4955).
 */
export const FROM_MARKETPLACE_TEAM_LABEL = "From Marketplace Team";
const UnnamedMemberName = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontStyle: "italic",
}));
/** A member the team no longer has reads as a deletion, the way a removed row of the team redline does. */
const RemovedMemberName = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  textDecoration: "line-through",
  textDecorationColor: theme.palette.common.black,
  textDecorationThickness: "2px",
}));
const RemovedMemberAvatar = styled(IndividualAvatar)(({ theme }) => ({
  backgroundColor: theme.palette.grey[400],
}));
const TeamLeaderInput = styled(TextField, {
  shouldForwardProp: (prop) => prop !== "pendingChange",
})<{ pendingChange?: boolean }>(({ theme, pendingChange }) => ({
  [theme.breakpoints.down("md")]: {
    minWidth: "unset",
    width: "100%",
  },
  margin: theme.spacing(1, 0),
  minWidth: theme.spacing(25),
  ...(pendingChange && pendingChangeOutlinedFieldStyles(theme)),
}));
const TeamLoader = styled(Loader)(() => ({
  justifyContent: "flex-start",
}));
const selectionPanelWidth = "min(500px, 100vw)";
const Drawer = styled(MuiDrawer)(({ theme }) => ({
  flexShrink: 0,
  "& .MuiDrawer-paper": {
    boxSizing: "border-box",
    height: "calc(100% - 64px)",
    maxWidth: "100vw",
    overflow: "hidden",
    top: "64px",
    width: selectionPanelWidth,
    [theme.breakpoints.down("lg")]: {
      height: `calc(100% - ${theme.spacing(6)})`,
      top: theme.spacing(6),
    },
  },
}));
const SelectionPanel = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== "closeAfterTransition",
})<{ open?: boolean }>(({ theme, open }) => ({
  "& .MuiDrawer-paper": {
    overflowX: "hidden",
    transition: theme.transitions.create("width", {
      easing: open ? theme.transitions.easing.easeOut : theme.transitions.easing.sharp,
      duration: open ? theme.transitions.duration.enteringScreen : theme.transitions.duration.leavingScreen,
    }),
    width: open ? selectionPanelWidth : 0,
  },
}));
const SelectionPanelContainer = styled("div")(() => ({
  display: "flex",
  flex: 1,
  flexDirection: "column",
  height: "100%",
  maxWidth: "100vw",
  minHeight: 0,
  width: selectionPanelWidth,
}));
const SelectionPanelTitleBar = styled("div")(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  padding: theme.spacing(2, 2, 1),
}));
const SelectionPanelContent = styled("div")(() => ({
  display: "flex",
  flex: 1,
  minHeight: 0,
  overflow: "hidden",
}));
const SelectionPanelActions = styled("div")(({ theme }) => ({
  backgroundColor: "#FAFAFA",
  padding: theme.spacing(1.5, 2, 10),
}));
const SelectionPanelCancelButton = styled(Button)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.primary.main, 0.04),
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
  },
}));

type SelectionPanelType = "leader" | "member" | "marketplaceTeam";

/** A roster member as the team holds them, beside the person the directory could make of them. */
type RosterMember = {
  member: EntityMember;
  individual?: Individual;
};

/**
 * A vendor-team member's canonical collection identity is the compound `userId:legalEntityId` (see
 * proposal-operation-identity.ts), not just the userId `ProposalField.TeamMember` carries, so the
 * dropdown is told that identity explicitly via `collectionItemId`.
 */
function memberCollectionItemId(member: Individual | EntityMember | undefined): string | undefined {
  if (!member?.userId || !member.legalEntityId) return undefined;
  return `${member.userId.value}:${member.legalEntityId.value}`;
}

/**
 * Everything the roster's Name column should match a person on. A search key only: the column is
 * ORDERED by the row's `sortText`, so that two members whose names tie are not separated by the
 * address appended here (#5164).
 */
function rosterSearchText(individual: Individual | undefined, fallback: string): string {
  if (!individual) return fallback;
  const email = individual.email?.value ?? "";
  return [individual.fullName, email].filter((word) => word.length > 0).join(" ");
}

type EditVendorTeamProps = {
  className?: string;
  leader: EntityMember | null;
  members: EntityMember[];
  disableEditing?: boolean;
  fieldLockTooltip?: string;
  navigate?: NavigateFunction;
  searchParams?: URLSearchParams;
  /** Hides the per-field change-history controls beside the leader, each member, and the team header. */
  hideFieldHistory?: boolean;
  /**
   * Whether this actor may change WHICH TEAM this is — its leader and its marketplace identity — as
   * opposed to who is on it. The two are different permissions on a proposal: the roster is
   * negotiable content that anyone with proposal edit rights may change, while the team's identity
   * is initial proposal setup, reserved to the creator until the proposal is first submitted
   * (`canConfigureInitialProposalSetup`).
   *
   * When false the leader controls and "From Marketplace Team" are withdrawn while the member
   * controls stay live, because offering them produced edits the save path then discarded in
   * silence (#4854). Defaults to true, so callers with no such distinction — the team-template
   * editor, where the team IS the thing being authored — are unaffected.
   */
  canChangeTeamIdentity?: boolean;
  pendingChangeFields?: ProposalField[];
  /**
   * Whether this team is restricted. Not a rule about who may join — it decides what the roster
   * costs, because a restricted team's every member must approve the proposal before it can be
   * hired. "Add all colleagues" says so before it adds anyone.
   */
  teamRestricted?: boolean;
  onLeaderUpdated?: (leader: EntityMember | null) => void;
  onMemberAdded?: (member: EntityMember) => void;
  /**
   * Called with several members to add in one change. Separate from `onMemberAdded` because a
   * caller accumulates additions onto a roster captured at render: told one at a time, it would keep
   * only the last. Without it, "add all colleagues" is offered only where a caller can take a batch.
   */
  onMembersAdded?: (members: EntityMember[]) => void;
  /**
   * Called with the member to take off the roster.
   *
   * The id is still optional, and every caller still reads a missing one as "clear the roster" — but
   * nothing HERE calls it that way any more. The whole-roster trash beside the heading was one of the
   * two icon-only buttons #4955's review asked to be taken off the team tables, and removal is now
   * only ever decided on a member's own row. The callers keep their clear-all branch because it is
   * their own contract rather than this view's, and narrowing it reaches three surfaces this change
   * has no business in.
   */
  onMemberRemoved?: (userId?: Guid) => void;
  /**
   * Called with a team member reverted to a prior value (parsed from that value's history entry). The
   * caller applies it through the same builder setter as any other member edit, so the revert flows
   * through the same `queueProposalUpdate` path and lands as a `CollectionItemUpdated`/
   * `CollectionItemAdded` op — never a raw `FieldSet`.
   */
  onMemberReverted?: (member: EntityMember) => void;
  /**
   * Called with the leader reverted to a prior value (parsed from that value's history entry). The
   * caller applies it through the leader's own setter (`updateTeamOnProposalBuilder`, the same one
   * `onLeaderUpdated` uses), so the revert flows through `queueProposalUpdate` as a plain
   * `/vendorTeam/leader` `FieldSet` — never a raw whole-team overwrite touching `members`.
   *
   * `/vendorTeam/leader` is a scalar field (see proposal-command-builder.ts), not a collection item, so
   * this is routed through `ProposalFieldTimelineDropdown`'s `scalarPath`/structured-scalar mode rather
   * than its collection-item mechanism: deriving an `itemId` from `ProposalField.TeamLeader(id)`'s id
   * would match no event (scalar `FieldSet` events never carry an `itemId`) and render a permanently
   * empty menu.
   */
  onLeaderReverted?: (leader: EntityMember) => void;
  onMarketplaceTeamSelected?: (team: MarketplaceTeam) => void;
  negotiationId?: string;
  viewerScope?: string;
  topControls?: React.ReactNode;
  /**
   * Extra actions rendered in the same button row as "From Marketplace Team", so a caller's own
   * action (e.g. the proposal team tab's "Create Template") lines up with it instead of sitting in a
   * separate row above the team controls. Use `topControls` for non-button content.
   */
  toolbarActions?: React.ReactNode;
  selectionPresentation?: "dialog" | "drawer";
  onSelectionDrawerOpenChange?: (isOpen: boolean) => void;
};

/**
 * The vendor team's draft roster, drawn as ONE table — Name, Email, Company, Role — through the
 * shared `TeamRosterTable`, the way the Fee Schedule tab is one table (#4955).
 *
 * The leader and a member still differ in every way they did: the leader is a scalar proposal field
 * (`/vendorTeam/leader`) and a member is a collection item keyed by the compound
 * `userId:legalEntityId`, and they are governed by *different permissions* — see
 * `canChangeTeamIdentity`, where the roster belongs to anyone holding `canEditDraft` while the team's
 * identity is the creator's pre-submission setup (#4854). That difference is stated by the Role column
 * and enforced by the row's own controls, which is where it belongs; a second table said it by
 * geometry and cost the reader any view of the team as a whole.
 *
 * Used outside proposals too, by the team-template editor, where the team is the thing being authored
 * and every one of those distinctions is simply absent.
 */
export default function EditVendorTeam(props: Readonly<EditVendorTeamProps>) {
  const {
    className,
    leader,
    members,
    negotiationId,
    viewerScope,
    disableEditing,
    fieldLockTooltip,
    navigate,
    searchParams,
    hideFieldHistory,
    topControls,
    toolbarActions,
    pendingChangeFields,
    teamRestricted,
    onLeaderUpdated,
    onMemberAdded,
    onMembersAdded,
    onMemberRemoved,
    onMemberReverted,
    onLeaderReverted,
    onMarketplaceTeamSelected,
    onSelectionDrawerOpenChange,
  } = props;
  const selectionPresentation = props.selectionPresentation ?? "dialog";
  // Read off `props` rather than the destructuring above so the default reads as a default rather
  // than as a destructuring pattern the React Compiler cannot lower (see app/CLAUDE.md).
  const teamIdentityIsLocked = props.canChangeTeamIdentity === false;

  const memberUserIds: string[] = useMemo(() => {
    return members.map((member) => member.userId.value);
  }, [members]);

  // #region Removed members
  // The members the team no longer has, read from the team's own history and kept as struck-through,
  // read-only rows so each keeps the history of the member they were (#4954) — the whole-team history
  // control that used to be the only place they survived is gone. Nothing that edits the roster is
  // offered on them.
  const showFieldHistory = Boolean(!hideFieldHistory && negotiationId && viewerScope);
  const presentMemberItemIds = useMemo(
    () => members.flatMap((member) => {
      const itemId = proposalTeamMemberItemId(member);
      return itemId ? [itemId] : [];
    }),
    [members],
  );
  const removedMembers = useRemovedProposalTeamMembers({
    negotiationId: showFieldHistory ? negotiationId : undefined,
    viewerScope: showFieldHistory ? viewerScope : undefined,
    presentItemIds: presentMemberItemIds,
  });
  // #endregion

  const individualsToFetch = useMemo(() => {
    const ids = new Set<string>();
    if (leader) ids.add(leader.userId.value);
    memberUserIds.forEach((userId) => ids.add(userId));
    removedMembers.forEach((removed) => ids.add(removed.member.userId.value));
    return ids;
  }, [leader, memberUserIds, removedMembers]);

  const individualQuery = useGetIndividualsByUserIdsQuery(
    individualsToFetch.size ? { userIds: individualsToFetch } : skipToken,
  );

  const individuals: Individual[] = useMemo(() => {
    return Individual.fromJSONArray(individualQuery.data) ?? [];
  }, [individualQuery.data]);

  const individualMap: Map<string, Individual> = useMemo(() => {
    const map = new Map<string, Individual>();
    individuals.forEach((individual) => {
      if (individual.userId?.value) {
        map.set(individual.userId.value, individual);
      }
    });
    return map;
  }, [individuals]);

  const leaderIndividual: Individual | undefined = useMemo(() => {
    if (!leader) return undefined;
    return individualMap.get(leader.userId.value);
  }, [leader, individualMap]);

  /**
   * The roster as the team holds it, each member beside whatever the directory could make of them.
   *
   * Built from `members`, never from the directory's answer: pairing the two and keeping the pair is
   * what stops a member the lookup cannot resolve from vanishing off the roster altogether.
   */
  const memberRoster: RosterMember[] = useMemo(() => {
    return members.map((member) => ({ member, individual: individualMap.get(member.userId.value) }));
  }, [members, individualMap]);

  const userPermissions = useSelector(getSessionPermissions);

  const [teamLeaderSelectorAnchor, setTeamLeaderSelectorAnchor] = React.useState<HTMLDivElement>();
  const [selectionPanel, setSelectionPanel] = React.useState<SelectionPanelType>();
  const [selectionPanelFilterViewOpen, setSelectionPanelFilterViewOpen] = React.useState(false);
  const hasAnyLeaderPendingChange =
    pendingChangeFields?.some((field) => field.name === ProposalFieldName.TeamLeader) ?? false;
  const hasAnyMemberPendingChange =
    pendingChangeFields?.some((field) => field.name === ProposalFieldName.Team) ?? false;
  // A lock column, not a lock avatar: the avatar is the person's own mark, and swapping it out told
  // the reader who was locked at the cost of no longer telling them who it was.
  const showLockColumn = Boolean(disableEditing && fieldLockTooltip);

  const confirm = useConfirmDialog();
  const { openDialog, popDialog } = useDialog();
  const session = useSession();

  const closeSelectionPanel = useCallback(() => {
    setSelectionPanel(undefined);
    setSelectionPanelFilterViewOpen(false);
    onSelectionDrawerOpenChange?.(false);
  }, [onSelectionDrawerOpenChange]);

  const openSelectionPanel = useCallback(
    (panel: SelectionPanelType) => {
      setSelectionPanel(panel);
      setSelectionPanelFilterViewOpen(false);
      onSelectionDrawerOpenChange?.(true);
    },
    [onSelectionDrawerOpenChange],
  );

  React.useEffect(() => {
    return () => onSelectionDrawerOpenChange?.(false);
  }, [onSelectionDrawerOpenChange]);

  /**
   * Checks if the user with the given id is already selected or invited to the vendor-team
   *
   * Asked of the roster the team holds rather than of the people the directory could name, so a member
   * whose lookup failed still blocks a second row for the same person.
   *
   * @param userId Id of the user to check
   * @returns True if the user is already selected or invited to the vendor-team, false otherwise
   */
  const getIsMemberAlreadyOnTeam = useCallback(
    (userId?: Guid): boolean => {
      if (!userId) return false;

      return (
        members.some((member) => member.userId?.isEqualTo(userId)) ||
        (leaderIndividual?.userId?.isEqualTo(userId) ?? false)
      );
    },
    [leaderIndividual, members],
  );

  const getSelectedTeamMemberUserIds = useCallback((): Guid[] => {
    const userIds: Guid[] = members.map((member) => member.userId);
    if (leaderIndividual?.userId) {
      userIds.push(leaderIndividual.userId);
    }
    return userIds;
  }, [leaderIndividual, members]);

  /**
   * Handles when a new leader has been selected
   *
   * @param leader The new leader to replace the current leader with
   */
  const handleNewLeaderSelected = useCallback(
    async (leader: Individual) => {
      if (leaderIndividual) {
        const response = await confirm({
          title: "Replace Leader?",
          message: `Do you want to replace the current team leader with ${leader.fullName}?`,
          okButtonText: "Replace",
        });

        if (response === ConfirmResponse.Cancel) return;
      }

      if (members.some((member) => member.userId?.isEqualTo(leader.userId))) {
        onMemberRemoved?.(leader.userId);
        onLeaderUpdated?.(leader.toEntityMember());
        return;
      }

      onLeaderUpdated?.(leader?.toEntityMember());
    },
    [leaderIndividual, members, onLeaderUpdated, confirm, onMemberRemoved],
  );

  /**
   * Handles what happens when add leader is clicked
   */
  const beginLeaderSelection = useCallback(() => {
    if (selectionPresentation === "drawer") {
      openSelectionPanel("leader");
      return;
    }

    openDialog({
      title: "Select Leader",
      component: (
        <VendorTeamMemberSelection
          onNewSelection={handleNewLeaderSelected}
          selectLeader={true}
          selectMember={false}
          selectedTeamMemberUserIds={getSelectedTeamMemberUserIds()}
          searchParams={searchParams}
          closeDialog={popDialog}
        />
      ),
      contentSxProps: {
        display: "flex",
        minHeight: "60vh",
      },
      MuiProps: {
        fullWidth: true,
        maxWidth: "lg",
      },
    });
  }, [
    getSelectedTeamMemberUserIds,
    handleNewLeaderSelected,
    openSelectionPanel,
    searchParams,
    selectionPresentation,
    openDialog,
    popDialog,
  ]);

  const handleCoworkerSelectedAsLeader = useCallback(
    async (leader?: Individual) => {
      if (!leader) {
        onLeaderUpdated?.(null);
        return;
      }
      onLeaderUpdated?.(leader?.toEntityMember());
    },
    [onLeaderUpdated],
  );

  function handleSelectTeamLeaderClicked(event: React.MouseEvent<HTMLDivElement>) {
    const inputElements = event.currentTarget.getElementsByTagName("input");
    setTeamLeaderSelectorAnchor(inputElements.length > 0 ? inputElements[0] : event.currentTarget);
  }

  /**
   * Handles when a new member has been selected
   *
   * @param teamMember The new member to add to the vendor-team
   */
  const handleNewMemberSelected = useCallback(
    (teamMember: Individual) => {
      if (getIsMemberAlreadyOnTeam(teamMember.userId)) {
        enqueueSnackbar("Selected user is already on vendor-team", {
          variant: "info",
          preventDuplicate: true,
          autoHideDuration: 7500,
        });
        return;
      }

      onMemberAdded?.(teamMember.toEntityMember());
    },
    [getIsMemberAlreadyOnTeam, onMemberAdded],
  );

  /**
   * Handles what happens when add member is clicked
   */
  const beginMemberSelection = useCallback(() => {
    if (selectionPresentation === "drawer") {
      openSelectionPanel("member");
      return;
    }

    openDialog({
      title: "Select Member",
      component: (
        <VendorTeamMemberSelection
          navigate={navigate}
          searchParams={searchParams}
          onNewSelection={handleNewMemberSelected}
          selectLeader={false}
          selectMember={true}
          selectedTeamMemberUserIds={getSelectedTeamMemberUserIds()}
          closeDialog={popDialog}
        />
      ),
      contentSxProps: {
        display: "flex",
        minHeight: "60vh",
      },
      MuiProps: {
        fullWidth: true,
        maxWidth: "lg",
      },
    });
  }, [
    getSelectedTeamMemberUserIds,
    handleNewMemberSelected,
    navigate,
    openSelectionPanel,
    searchParams,
    selectionPresentation,
    openDialog,
    popDialog,
  ]);

  /**
   * Handles what happens when a member is removed from the vendor-team
   *
   */
  const handleLeaderRemoved = useCallback(async () => {
    const response = await confirm({
      title: "Remove Leader?",
      message: `Do you want to remove the leader from the team?`,
      okButtonText: "Remove",
    });

    if (response === ConfirmResponse.Cancel) return;

    onLeaderUpdated?.(null);
  }, [confirm, onLeaderUpdated]);

  /**
   * Handles what happens when a member is removed from the vendor-team
   *
   * @param userId The userId of the member to remove
   */
  const handleMemberRemoved = useCallback(
    async (userId?: Guid) => {
      if (!userId) return;

      const response = await confirm({
        title: "Remove Member?",
        message: `Do you want to remove the member from the team?`,
        okButtonText: "Remove",
      });

      if (response === ConfirmResponse.Cancel) return;

      onMemberRemoved?.(userId);
    },
    [confirm, onMemberRemoved],
  );

  /**
   * Opens the user profile dialog for the given individual
   *
   * @param individualId The id of the individual to open the dialog for
   */
  const openUserProfileDialog = useCallback(
    (individualId: Guid) => {
      openDialog({
        component: <ViewIndividualProfile individualId={individualId} />,
        titleStyle: {
          paddingBottom: 0,
        },
        contentSxProps: {
          display: "flex",
          overflowX: "hidden",
        },
        MuiProps: {
          maxWidth: "lg",
          fullWidth: true,
        },
      });
    },
    [openDialog],
  );

  const handleMemberRevert = useCallback(
    (entry: ProposalFieldTimelineRevertEntry) => {
      // This dropdown instance is always in collection-item mode (a team member), so `entry.operation`
      // is always CollectionItemAdded/Updated at runtime — narrowed here to satisfy
      // `proposalCollectionItemRevertValue`'s parameter type, which excludes `FieldSet`.
      if (entry.operation?.kind !== "CollectionItemAdded" && entry.operation?.kind !== "CollectionItemUpdated") return;
      const revertValue = proposalCollectionItemRevertValue(entry.operation);
      if (revertValue === undefined) return;
      const revertedMember = EntityMember.fromJSON(revertValue as never);
      if (!revertedMember) return;
      onMemberReverted?.(revertedMember);
    },
    [onMemberReverted],
  );

  // `entry.operation` is a `FieldSet` built by `buildProposalScalarFieldRevertOperation` (structured
  // scalar mode — see proposal-field-timeline-dropdown.tsx's `scalarPath`); only its `value` is used,
  // the same way `handleMemberRevert` only uses its collection operation's `value`.
  const handleLeaderRevert = useCallback(
    (entry: ProposalFieldTimelineRevertEntry) => {
      // This dropdown instance is always in structured-scalar mode (the team leader), so
      // `entry.operation` is always FieldSet at runtime — narrowed here to satisfy
      // `proposalScalarFieldRevertValue`'s parameter type, which excludes the collection-item kinds.
      if (entry.operation?.kind !== "FieldSet") return;
      const revertValue = proposalScalarFieldRevertValue(entry.operation);
      if (revertValue === undefined) return;
      const revertedLeader = EntityMember.fromJSON(revertValue as never);
      if (!revertedLeader) return;
      onLeaderReverted?.(revertedLeader);
    },
    [onLeaderReverted],
  );

  function leaderRosterRow(leader: Individual): TeamRosterRow {
    const field = ProposalField.TeamLeader(leader.userId);
    const hasPendingChange = fieldHasPendingChange(pendingChangeFields, field);

    return {
      id: "vendor-team-leader",
      searchText: rosterSearchText(leader, ""),
      sortText: leader.fullName,
      rowSx: hasPendingChange ? (theme) => pendingChangeTableRowStyles(theme) : undefined,
      avatar: <IndividualAvatar individual={leader} />,
      // `RowLink`, the one way a name cell says it opens something — see #5244. The hand-rolled
      // link it replaces was an anchor with no `href`, which Enter and Space do not activate.
      name: (
        <RowLink ariaLabel={leader.fullName} onClick={() => openUserProfileDialog(leader.id)}>
          {leader.fullName}
        </RowLink>
      ),
      email: teamRosterCell(leader.email?.value),
      company: teamRosterCell(leader.entityName),
      role: { content: TEAM_LEADER_ROLE },
      actions: (
        <>
          {!disableEditing && !teamIdentityIsLocked && (
            <Tooltip title="Remove Leader">
              <span>
                <ProposalDeleteActionButton aria-label="Remove Leader" onClick={handleLeaderRemoved} />
              </span>
            </Tooltip>
          )}
          {!hideFieldHistory && (
            <>
              {/* Reverting the leader IS changing the leader, so an actor who may not change the
                  team's identity gets the read-only history instead of the revert menu. */}
              {disableEditing || teamIdentityIsLocked ? (
                <ProposalFieldHistoryButton field={field} negotiationId={negotiationId} viewerScope={viewerScope} />
              ) : (
                <ProposalFieldTimelineDropdown
                  field={ProposalField.TeamLeader()}
                  scalarPath="/vendorTeam/leader"
                  negotiationId={negotiationId}
                  viewerScope={viewerScope}
                  onRevert={handleLeaderRevert}
                />
              )}
            </>
          )}
        </>
      ),
    };
  }

  /**
   * A member the team no longer has: struck through, with nothing to remove or revert, and the history
   * of the member they were — scoped to them — as the row's one control (#4954).
   */
  function removedMemberRosterRow(removed: ProposalTeamRemovedMember): TeamRosterRow {
    const individual = individualMap.get(removed.member.userId.value);
    const displayName = individual?.fullName ?? (individualQuery.isLoading ? "Loading..." : "Former member");
    return {
      id: `removed-${removed.itemId}`,
      searchText: rosterSearchText(individual, displayName),
      sortText: displayName,
      rowAttributes: { "data-removed-member": removed.itemId },
      avatar: <RemovedMemberAvatar individual={individual} />,
      name: <RemovedMemberName>{displayName}</RemovedMemberName>,
      email: teamRosterCell(individual?.email?.value),
      company: teamRosterCell(individual?.entityName),
      role: { content: TEAM_MEMBER_ROLE },
      actions: (
        <ProposalFieldHistoryButton
          field={ProposalField.TeamMember(removed.member.userId)}
          collectionItemId={removed.itemId}
          negotiationId={negotiationId}
          viewerScope={viewerScope}
        />
      ),
    };
  }

  /**
   * One member of the roster.
   *
   * A member the directory could not name keeps their row and every control on it — the actor has to be
   * able to see them and to take them off the team. Only the name and the profile link, which need an
   * individual, stand down.
   */
  function memberRosterRow(rosterMember: RosterMember): TeamRosterRow {
    const member = rosterMember.member;
    const individual = rosterMember.individual;
    const field = ProposalField.TeamMember(member.userId);
    const hasPendingChange = fieldHasPendingChange(pendingChangeFields, field);
    // The member's own history, not the whole roster's: a member's `ProposalField` carries their
    // user id, but their timeline path is the team's (#4954).
    const collectionItemId = memberCollectionItemId(individual ?? member);

    return {
      id: collectionItemId ?? member.userId.value,
      searchText: rosterSearchText(individual, UNNAMED_MEMBER),
      sortText: individual?.fullName ?? UNNAMED_MEMBER,
      rowSx: hasPendingChange ? (theme) => pendingChangeTableRowStyles(theme) : undefined,
      avatar: <IndividualAvatar individual={individual} />,
      name: individual ? (
        <RowLink ariaLabel={individual.fullName} onClick={() => openUserProfileDialog(individual.id)}>
          {individual.fullName}
        </RowLink>
      ) : (
        <UnnamedMemberName>{UNNAMED_MEMBER}</UnnamedMemberName>
      ),
      // A member the directory could not name has no email and no firm to show either, and their
      // membership's GUIDs are not a stand-in for one — the columns say so with the roster's own mark.
      email: teamRosterCell(individual?.email?.value),
      company: teamRosterCell(individual?.entityName),
      role: { content: TEAM_MEMBER_ROLE },
      actions: (
        <>
          {!disableEditing && (
            <Tooltip title="Remove Member">
              <span>
                <ProposalDeleteActionButton
                  aria-label="Remove Member"
                  onClick={() => handleMemberRemoved(member.userId)}
                />
              </span>
            </Tooltip>
          )}
          {!hideFieldHistory && (
            <>
              {disableEditing ? (
                <ProposalFieldHistoryButton
                  field={field}
                  collectionItemId={collectionItemId}
                  negotiationId={negotiationId}
                  viewerScope={viewerScope}
                />
              ) : (
                <ProposalFieldTimelineDropdown
                  field={field}
                  negotiationId={negotiationId}
                  viewerScope={viewerScope}
                  collectionItemId={collectionItemId}
                  onRevert={handleMemberRevert}
                />
              )}
            </>
          )}
        </>
      ),
    };
  }

  const handleMarketplaceTeamSelected = useCallback(
    (team: MarketplaceTeam) => {
      if (!team.leader.profile) throw new Error("Team leader profile is undefined");

      onMarketplaceTeamSelected?.(team);
      if (selectionPresentation === "drawer") {
        closeSelectionPanel();
      } else {
        popDialog();
      }
    },
    [closeSelectionPanel, onMarketplaceTeamSelected, popDialog, selectionPresentation],
  );

  const beginMarketplaceTeamSelection = useCallback(() => {
    if (selectionPresentation === "drawer") {
      openSelectionPanel("marketplaceTeam");
      return;
    }

    openDialog({
      title: "Select Marketplace Team",
      component: <VendorTeamSelection onNewSelection={handleMarketplaceTeamSelected} closeDialog={popDialog} />,
      contentSxProps: {
        display: "flex",
        minHeight: "60vh",
      },
      MuiProps: {
        fullWidth: true,
        maxWidth: "lg",
      },
    });
  }, [handleMarketplaceTeamSelected, openDialog, openSelectionPanel, popDialog, selectionPresentation]);

  // The roster's own ids, not `getSelectedTeamMemberUserIds()`: that one reads the leader from the
  // directory lookup, which can be pending or have failed, and a leader missing from the list would be
  // added as a member — which `VendorTeam.ValidateInputAsync` refuses outright.
  const alreadyOnTeamUserIds = useMemo(
    () => [...members.map((member) => member.userId), ...(leader ? [leader.userId] : [])],
    [leader, members],
  );
  const addAllColleagues = useAddAllColleagues({
    alreadyOnTeamUserIds,
    teamRestricted,
    onColleaguesChosen: (colleagues) => onMembersAdded?.(colleagues),
  });

  function getSelectionPanelTitle(): string {
    switch (selectionPanel) {
      case "leader":
        return "Select Leader";
      case "member":
        return "Select Member";
      case "marketplaceTeam":
        return "Select Marketplace Team";
      default:
        return "";
    }
  }

  function renderSelectionPanelContent() {
    if (selectionPanel === "leader") {
      return (
        <VendorTeamMemberSelection
          onNewSelection={handleNewLeaderSelected}
          selectLeader={true}
          selectMember={false}
          selectedTeamMemberUserIds={getSelectedTeamMemberUserIds()}
          searchParams={searchParams}
          closeDialog={closeSelectionPanel}
        />
      );
    }

    if (selectionPanel === "member") {
      return (
        <VendorTeamMemberSelection
          navigate={navigate}
          searchParams={searchParams}
          onNewSelection={handleNewMemberSelected}
          selectLeader={false}
          selectMember={true}
          selectedTeamMemberUserIds={getSelectedTeamMemberUserIds()}
          closeDialog={closeSelectionPanel}
        />
      );
    }

    if (selectionPanel === "marketplaceTeam") {
      return (
        <VendorTeamSelection
          searchParams={searchParams}
          filterPresentation="contained"
          onFilterViewOpenChange={setSelectionPanelFilterViewOpen}
          onNewSelection={handleMarketplaceTeamSelected}
          closeDialog={closeSelectionPanel}
        />
      );
    }

    return null;
  }

  // Choosing a marketplace team replaces the team's identity, not just its roster, so it belongs
  // with the leader controls behind `canChangeTeamIdentity`.
  const showMarketplaceTeamButton = Boolean(
    userPermissions?.includesAccessToMarketplaceSearch &&
      !disableEditing &&
      !teamIdentityIsLocked &&
      session.accountType === AccountType.Client,
  );

  const viewingAsVendor = Boolean(session.context?.viewingAsVendor);
  const leaderIsLoading = Boolean(individualQuery.isLoading && leader && !leaderIndividual);
  // A vendor reading its own team picks the leader through a field above the roster, so the leader is
  // not a row there — everything else is one table.
  const leaderRows = leaderIndividual && !viewingAsVendor ? [leaderRosterRow(leaderIndividual)] : [];
  const showSelectLeader =
    !leaderIndividual && !viewingAsVendor && !disableEditing && !teamIdentityIsLocked && !leaderIsLoading;

  const membersAreLoading =
    individualQuery.isLoading && members.length > 0 && memberRoster.every((entry) => !entry.individual);
  const rosterRows: TeamRosterRow[] = [
    ...leaderRows,
    ...(membersAreLoading ? [] : memberRoster.map(memberRosterRow)),
    ...removedMembers.map(removedMemberRosterRow),
  ];
  const rosterIsLoading = membersAreLoading || (leaderIsLoading && !viewingAsVendor);
  const showAddMember = !disableEditing && !membersAreLoading;
  // Whose roster this is, read off who is on it. A client may edit the vendor team too — the roster
  // is negotiable content — so without this the action would offer to put the client's own staff on
  // the firm's team. `onMembersAdded` is the other half: a caller that can only take one member at a
  // time cannot apply a batch, so it is not offered one.
  const showAddAllColleagues = Boolean(
    showAddMember && onMembersAdded && mayAddAllColleagues(session.user?.legalEntityId, rosterLegalEntityId(leader, members)),
  );
  const addActions: TeamRosterAddAction[] = [];
  // Two adds on one toolbar, and they are not the same permission: "Select Leader" is the creator's
  // pre-submission setup, "Add Member" is the roster any `canEditDraft` actor may change (#4854).
  if (showSelectLeader) {
    addActions.push({ label: "Select Leader", onClick: beginLeaderSelection, disabled: !onLeaderUpdated });
  }
  if (showAddMember) {
    addActions.push({ label: "Add Member", onClick: beginMemberSelection });
  }
  // The other ways this roster gains people, behind the add control's dropdown rather than as buttons
  // of their own (#4955): "Add All Colleagues" replaces the icon-only button that sat beside the
  // heading, and "From Marketplace Team" the outlined button that sat above it. Both are still a way
  // of filling the team, which is what the split button's dropdown collects.
  if (showAddAllColleagues) {
    addActions.push({
      label: ADD_ALL_COLLEAGUES_LABEL,
      icon: <GroupAddOutlinedIcon fontSize="small" />,
      loading: addAllColleagues.isWorking,
      onClick: addAllColleagues.addAllColleagues,
    });
  }
  if (showMarketplaceTeamButton) {
    addActions.push({
      label: FROM_MARKETPLACE_TEAM_LABEL,
      icon: <GroupsIcon fontSize="small" />,
      onClick: beginMarketplaceTeamSelection,
    });
  }

  // Drawn under the roster's heading, because it explains the absence of the leader controls whichever
  // shape the leader takes.
  const teamIdentityLockNotice = teamIdentityIsLocked && !disableEditing && (
    <TeamIdentityLockNotice>{TEAM_IDENTITY_LOCK_MESSAGE}</TeamIdentityLockNotice>
  );
  const leaderHeaderSx = hasAnyLeaderPendingChange ? (theme: Theme) => pendingChangeListItemStyles(theme) : undefined;
  // One heading over one table, so it lights up for a pending change to either half of the team. Which
  // half is still told apart by the row that carries the highlight.
  const rosterPendingChange = viewingAsVendor ? hasAnyMemberPendingChange : hasAnyLeaderPendingChange || hasAnyMemberPendingChange;
  const rosterHeaderSx = rosterPendingChange ? (theme: Theme) => pendingChangeListItemStyles(theme) : undefined;

  return (
    <MainContainer className={className}>
      <TeamControls>
        {topControls}
        {/* "From Marketplace Team" used to stand here as a button of its own. It is one of the ways
            this roster can be filled, so it is now an entry behind the roster's add control instead —
            see `addActions` above (#4955). */}
        {toolbarActions && <TeamControlsGroup>{toolbarActions}</TeamControlsGroup>}
      </TeamControls>
      <TeamContainer>
        {/* A vendor looking at its own team picks its leader by typing into a field and choosing a
            coworker from a popover, not off a roster — so the leader is a field above the roster
            rather than a row in it. */}
        {viewingAsVendor && (
          <TeamRosterSection title="Team Leader" headerSx={leaderHeaderSx}>
            {teamIdentityLockNotice}
            {!leaderIsLoading && (
              <>
                <TeamLeaderInput
                  pendingChange={hasAnyLeaderPendingChange}
                  disabled={teamLeaderSelectorAnchor !== undefined || disableEditing || teamIdentityIsLocked}
                  label={leaderIndividual ? "Team Leader" : "Select Team Leader"}
                  placeholder="No Leader Selected"
                  helperText="required"
                  required
                  autoComplete="off"
                  error={!leaderIndividual}
                  onClick={handleSelectTeamLeaderClicked}
                  value={leaderIndividual ? `${leaderIndividual?.fullName}` : ""}
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                />
                <TeamLeaderSelector
                  popoverAnchor={teamLeaderSelectorAnchor}
                  teamLeaderUserId={leaderIndividual?.userId}
                  onTeamLeaderUpdated={handleCoworkerSelectedAsLeader}
                  onPopoverClose={() => {
                    setTeamLeaderSelectorAnchor(undefined);
                  }}
                />
              </>
            )}
          </TeamRosterSection>
        )}
        <TeamRosterTable
          title="Team Roster"
          surface="draft-vendor-team"
          columns={TEAM_ROSTER_PERSON_COLUMNS}
          headerSx={rosterHeaderSx}
          notice={viewingAsVendor ? undefined : teamIdentityLockNotice}
          rows={rosterRows}
          lockIndicator={showLockColumn ? <ReviewerEditLockIcon title={fieldLockTooltip} /> : undefined}
          emptyMessage={rosterIsLoading ? <TeamLoader text="Loading..." /> : "No Team Members"}
          noMatchesMessage="No matching team members"
          // A row here carries two controls at most — remove and history. The default is sized for the
          // active proposal's five, and this tab shares its width with the Templates panel, so every
          // pixel the pinned controls column does not need is a pixel the Role column gets to show.
          actionsColumnWidth={ROSTER_ACTIONS_COLUMN_WIDTH}
          enableSearch
          addActions={addActions}
        />
        {/* No whole-team history here: a member's history lives on their row, and a removed member
            keeps a row of their own (#4954). No whole-roster "Remove All Members" either (#4955): it
            was an icon-only trash beside the heading, which the owner's review asked to be taken off
            the team tables, and removing a member is still offered on that member's own row. */}
      </TeamContainer>
      {selectionPresentation === "drawer" && (
        <SelectionPanel open={Boolean(selectionPanel)} anchor="right" variant="persistent" closeAfterTransition={false}>
          <SelectionPanelContainer>
            {!selectionPanelFilterViewOpen && (
              <SelectionPanelTitleBar>
                <Typography variant="h5">{getSelectionPanelTitle()}</Typography>
                <Tooltip title="Minimize panel">
                  <IconButton size="medium" onClick={closeSelectionPanel}>
                    <KeyboardTabIcon fontSize="large" />
                  </IconButton>
                </Tooltip>
              </SelectionPanelTitleBar>
            )}
            <SelectionPanelContent>{renderSelectionPanelContent()}</SelectionPanelContent>
            {!selectionPanelFilterViewOpen && (
              <SelectionPanelActions>
                <SelectionPanelCancelButton fullWidth variant="outlined" color="primary" onClick={closeSelectionPanel}>
                  Cancel
                </SelectionPanelCancelButton>
              </SelectionPanelActions>
            )}
          </SelectionPanelContainer>
        </SelectionPanel>
      )}
    </MainContainer>
  );
}
